import React, { useMemo, useState } from 'react';
import { GitMerge, AlertTriangle, Building2, Loader2, Megaphone, Download } from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';

/**
 * Give the accounts that have no bank the bank of their own branch.
 *
 * The Excel importer never wrote a bank field, so every account uploaded
 * before the bank selector existed has a branch but no bank -- and filtering
 * by bank found none of them. This repairs those records once, from the branch
 * they already name, rather than leaving every bank filter in the app to work
 * around the gap.
 */

/**
 * The notice that scrolls across the top of everyone's screen.
 *
 * Kept short on purpose: a ticker is for one sentence everybody should see
 * today, not for instructions. Anything longer belongs in Messages, where it
 * can be addressed, read and kept.
 */
const NoticeBannerSettings: React.FC = () => {
  const { noticeBanner, saveNoticeBanner, currentUser, banks, zones, users } = useSRMS();
  const [text, setText] = useState('');
  const [colour, setColour] = useState<'red' | 'amber' | 'green'>('green');
  const [noticeBank, setNoticeBank] = useState('');
  const [noticeZone, setNoticeZone] = useState('');
  // More than one agent can be named. A notice usually concerns a few people
  // working the same files, not exactly one.
  const [noticeAgents, setNoticeAgents] = useState<string[]>([]);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  React.useEffect(() => {
    setText(noticeBanner?.text || '');
    setColour(noticeBanner?.colour || 'green');
    setNoticeBank(noticeBanner?.bank || '');
    setNoticeZone(noticeBanner?.zone || '');
    setNoticeAgents(noticeBanner?.agents || []);
  }, [
    noticeBanner?.text,
    noticeBanner?.colour,
    noticeBanner?.bank,
    noticeBanner?.zone,
    noticeBanner?.agents,
  ]);

  /**
   * The agents who can be named, narrowed by whichever bank and zone are
   * chosen -- so the list cannot offer somebody the bank/zone rules would
   * then exclude anyway.
   */
  const agentOptions = React.useMemo(() => {
    const same = (a?: string, b?: string) =>
      (a || '').trim().replace(/\s+/g, ' ').toLowerCase() ===
      (b || '').trim().replace(/\s+/g, ' ').toLowerCase();

    return (users || [])
      .filter((u: any) => u.role === 'agent' || u.role === 'recovery_agent')
      .filter((u: any) => {
        if (!noticeBank) return true;
        const mine = (u.banks && u.banks.length ? u.banks : [u.bank]).filter(Boolean);
        // Somebody with no bank recorded is unclassified, not excluded.
        return mine.length === 0 || mine.some((b: string) => same(b, noticeBank));
      })
      .filter((u: any) => !noticeZone || !u.zone || same(u.zone, noticeZone))
      .map((u: any) => ({
        key: String(u.agentId || u.id || '').trim(),
        name: u.name || u.username || u.agentId || u.id,
        branch: u.branch || '',
      }))
      .filter((a: any) => a.key !== '')
      .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)));
  }, [users, noticeBank, noticeZone]);

  const toggleAgent = (key: string) =>
    setNoticeAgents((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  /** Zones narrow to the chosen bank, so the pair cannot contradict itself. */
  const zoneOptions = React.useMemo(() => {
    const same = (a?: string, b?: string) =>
      (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();
    const list = (zones || []).filter(
      (z: any) => !noticeBank || same(z.bankName, noticeBank)
    );
    return Array.from(
      new Set(list.map((z: any) => (z.name || '').trim()).filter(Boolean))
    ).sort();
  }, [zones, noticeBank]);

  if (currentUser.role !== 'admin') return null;

  const publish = async (clearIt = false) => {
    setBusy(true);
    try {
      const res = await saveNoticeBanner(clearIt ? '' : text, colour, {
        bank: clearIt ? '' : noticeBank,
        zone: clearIt ? '' : noticeZone,
        agents: clearIt ? [] : noticeAgents,
      });
      setNote(res.message);
      if (res.success && clearIt) {
        setText('');
        setNoticeAgents([]);
      }
      window.setTimeout(() => setNote(null), 6000);
    } finally {
      setBusy(false);
    }
  };

  const swatches: Array<{ key: 'red' | 'amber' | 'green'; label: string; dot: string }> = [
    { key: 'red', label: 'Urgent', dot: 'bg-rose-500' },
    { key: 'amber', label: 'Attention', dot: 'bg-amber-500' },
    { key: 'green', label: 'Information', dot: 'bg-emerald-500' },
  ];

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center gap-2">
        <Megaphone className="w-4 h-4 text-blue-600" />
        <div>
          <span className="font-bold text-slate-900 block">Scrolling notice</span>
          <span className="text-slate-500 text-[11px]">
            Shown at the top of the screen until you clear it. Leave it empty and the
            space stays blank. Aim it at a bank, a zone, or particular agents &mdash;
            naming agents means only they see it.
          </span>
        </div>
      </div>

      {note && (
        <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-900 font-semibold">
          {note}
        </div>
      )}

      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2.5 text-[11px]">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 160))}
          placeholder="e.g. OTS scheme closes on 28 September. Collect pending files today."
          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
        <div className="text-slate-400">{text.length} of 160 characters</div>

        {/* Who sees it. Blank means everybody; a bank narrows it to that
            bank's people, and a zone narrows it again within that bank.
            Administrators always see every notice, so a targeted one can be
            checked without pretending to be somebody else. */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={noticeBank}
            onChange={(e) => {
              setNoticeBank(e.target.value);
              setNoticeZone('');
            }}
            className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 font-medium focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
          >
            <option value="">Everyone (all banks)</option>
            {(banks || []).map((b: any) => (
              <option key={b.id || b.name} value={b.name}>
                {b.name}
              </option>
            ))}
          </select>

          <select
            value={noticeZone}
            onChange={(e) => setNoticeZone(e.target.value)}
            disabled={zoneOptions.length === 0}
            className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 font-medium focus:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer disabled:opacity-40"
          >
            <option value="">{noticeBank ? 'All zones in this bank' : 'All zones'}</option>
            {zoneOptions.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>

          {/* Named agents. Ticking any of them means ONLY they see the
              notice, whatever the bank and zone above say. */}
          <button
            type="button"
            onClick={() => setShowAgentPicker((v) => !v)}
            disabled={agentOptions.length === 0}
            className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 font-medium hover:bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer disabled:opacity-40 flex items-center gap-1.5"
          >
            <span>
              {noticeAgents.length === 0
                ? 'All agents'
                : `${noticeAgents.length} agent${noticeAgents.length === 1 ? '' : 's'} only`}
            </span>
            <span className="text-slate-400">{showAgentPicker ? '▲' : '▼'}</span>
          </button>
        </div>

        {showAgentPicker && agentOptions.length > 0 && (
          <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-slate-500">
                Tick the agents this notice is for. Tick none and every agent in
                the chosen bank or zone sees it.
              </span>
              {noticeAgents.length > 0 && (
                <button
                  type="button"
                  onClick={() => setNoticeAgents([])}
                  className="text-rose-600 hover:text-rose-700 font-semibold underline cursor-pointer shrink-0"
                >
                  clear
                </button>
              )}
            </div>
            <div className="max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-x-3">
              {agentOptions.map((a: any) => (
                <label
                  key={a.key}
                  className="flex items-center gap-2 py-1 cursor-pointer hover:bg-white rounded px-1"
                >
                  <input
                    type="checkbox"
                    checked={noticeAgents.includes(a.key)}
                    onChange={() => toggleAgent(a.key)}
                    className="cursor-pointer"
                  />
                  <span className="font-semibold text-slate-800 truncate">{a.name}</span>
                  <span className="text-slate-400 font-mono text-[10px] shrink-0">{a.key}</span>
                  {a.branch && (
                    <span className="text-slate-400 truncate">{a.branch}</span>
                  )}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {swatches.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setColour(s.key)}
              className={`px-2.5 py-1 rounded-lg border font-bold flex items-center gap-1.5 transition cursor-pointer ${
                colour === s.key
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${s.dot}`} />
              <span>{s.label}</span>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => publish(false)}
            disabled={busy || !text.trim()}
            className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold transition cursor-pointer"
          >
            {busy
              ? 'Publishing…'
              : noticeAgents.length > 0
              ? `Publish to ${noticeAgents.length} agent${noticeAgents.length === 1 ? '' : 's'}`
              : noticeBank
              ? 'Publish to this bank'
              : 'Publish to everyone'}
          </button>
          {noticeBanner?.text && (
            <button
              type="button"
              onClick={() => publish(true)}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 font-bold text-slate-700 transition cursor-pointer"
            >
              Clear the notice
            </button>
          )}
          {noticeBanner?.updatedBy && noticeBanner?.text && (
            <span className="text-slate-400">
              Posted by {noticeBanner.updatedBy}
              {noticeBanner.agents && noticeBanner.agents.length > 0
                ? ` · ${noticeBanner.agents.length} named agent${
                    noticeBanner.agents.length === 1 ? '' : 's'
                  }`
                : noticeBanner.bank
                ? ` · ${noticeBanner.bank}${noticeBanner.zone ? ` / ${noticeBanner.zone}` : ''}`
                : ' · everyone'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * REPAIRING BALANCES THAT ARE ALREADY WRONG.
 *
 * Fixing the arithmetic stops new damage; it does nothing for figures
 * already written to the database. This recalculates each loaded account
 * from its own original balance, the collections that actually exist, and
 * the settlement agreed on it.
 *
 * It shows the difference BEFORE writing. A repair that silently rewrites
 * money is not something anybody should have to trust.
 */
const BalanceRepair: React.FC = () => {
  const { accounts, accountsScope, previewBalanceRepair, applyBalanceRepair, currentUser } =
    useSRMS();
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState<any[] | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (currentUser.role !== 'admin' && (currentUser.role as string) !== 'super_admin') return null;

  const inr = (n: number) =>
    '\u20b9' + Math.round(Number(n) || 0).toLocaleString('en-IN');

  const check = () => {
    setBusy(true);
    setDone(null);
    try {
      setRows(previewBalanceRepair());
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!rows || rows.length === 0) return;
    const ok = window.confirm(
      `Recalculate ${rows.length} account(s)?\n\n` +
        'Each one is worked out again from its own collections. This changes ' +
        'balances that are currently wrong. It cannot be undone from here, ' +
        'though nothing is lost -- the collections themselves are untouched.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await applyBalanceRepair();
      setDone(res.message);
      setRows(previewBalanceRepair());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-rose-600" />
        <div>
          <span className="font-bold text-slate-900 block">
            Check and repair account balances
          </span>
          <span className="text-slate-500 text-[11px]">
            Checks BOTH figures an account carries: the loan balance and the OVERDUE --
            the red amount the accounts list prints above the grey &quot;Total&quot;.
            Until now only the balance was checked, which is why this kept reporting
            everything as healthy: deleting a collection restored the balance correctly
            but could never raise the overdue back, so the overdue stayed at whatever it
            fell to and the list went on showing it. Each account is worked out again
            from its own collections. An account that is already right is left alone.
          </span>
        </div>
      </div>

      {done && (
        <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-[11px] text-emerald-900 font-semibold">
          {done}
        </div>
      )}

      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2 text-[11px]">
        <div className="text-slate-600">
          Currently loaded: <strong>{accounts.length.toLocaleString('en-IN')}</strong> account(s)
          {accountsScope?.label ? ` \u2014 ${accountsScope.label}` : ''}.
        </div>
        <div className="text-slate-400">
          This checks the accounts loaded right now. To check every account in one pass,
          load them all in Loan Accounts first.
        </div>

        <button
          type="button"
          onClick={check}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 disabled:opacity-40 text-white font-bold text-[11px] transition cursor-pointer inline-flex items-center gap-1.5"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <span>Check balances (changes nothing)</span>
        </button>

        {rows && rows.length === 0 && (
          /* The count is named on purpose. "Nothing to repair" over an empty
             or half-loaded list is a clean bill of health nobody earned --
             an administrator's session holds no accounts until a bank or
             branch has been chosen, so this can be technically true and
             completely uninformative. */
          accounts.length === 0 ? (
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 font-bold">
              No accounts are loaded, so nothing was checked. Open Loan Accounts, choose a
              bank or branch, then come back.
            </div>
          ) : (
            <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-900 font-bold">
              All {accounts.length.toLocaleString('en-IN')} loaded account(s) agree with their
              collections. Nothing to repair.
              {accountsScope?.kind !== 'all' && (
                <span className="block font-normal text-emerald-800 mt-0.5">
                  This covered the accounts loaded now
                  {accountsScope?.label ? ` (${accountsScope.label})` : ''}, not the whole book.
                </span>
              )}
            </div>
          )
        )}

        {rows && rows.length > 0 && (
          <div className="space-y-2">
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 font-bold">
              {rows.length.toLocaleString('en-IN')} account(s) do not agree with their
              collections. Nothing has been changed yet.
              {rows.filter((r: any) => r.overdueOnly).length > 0 && (
                <span className="block font-normal text-amber-800 mt-0.5">
                  {rows.filter((r: any) => r.overdueOnly).length.toLocaleString('en-IN')} of
                  them have the right balance but a stale OVERDUE &mdash; the red figure on
                  the accounts list &mdash; left behind by a collection that was deleted.
                </span>
              )}
              {rows.filter((r: any) => r.originalWrong).length > 0 && (
                <span className="block font-normal text-rose-800 mt-1 pt-1 border-t border-amber-200">
                  <strong>
                    {rows.filter((r: any) => r.originalWrong).length.toLocaleString('en-IN')} of
                    them had their ORIGINAL loan balance overwritten by their own OTS
                    settlement
                  </strong>{' '}
                  &mdash; a silent write-off, and the one fault this check could not see
                  before, because such an account agrees with itself. The true figure has
                  been read back out of the OTS history. Check it against the bank&apos;s
                  own statement before repairing.
                </span>
              )}
            </div>

            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-left">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0">
                  <tr>
                    <th className="p-2">Account</th>
                    <th className="p-2 text-right">Original</th>
                    <th className="p-2 text-right">Collected</th>
                    <th className="p-2 text-right">Concession</th>
                    <th className="p-2 text-right">Balance now</th>
                    <th className="p-2 text-right">Should be</th>
                    <th className="p-2 text-right">Overdue now</th>
                    <th className="p-2 text-right">Should be</th>
                    <th className="p-2 text-right">Change</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-mono">
                  {rows.map((r) => (
                    <tr key={`rep-${r.accountId}`} className="hover:bg-slate-50">
                      <td className="p-2">
                        <div className="font-bold text-blue-700">{r.accountId}</div>
                        <div className="font-sans text-slate-500">{r.customerName}</div>
                        {r.reopening && (
                          <div className="font-sans text-[10px] text-amber-700 font-bold">
                            settled with nothing paid &mdash; will reopen
                          </div>
                        )}
                        {r.overdueOnly && (
                          <div className="font-sans text-[10px] text-rose-700 font-bold">
                            balance is correct &mdash; stale overdue from a deleted collection
                          </div>
                        )}
                        {r.originalWrong && (
                          <div className="font-sans text-[10px] text-rose-700 font-bold">
                            original loan balance was overwritten by its own OTS settlement
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right text-slate-700">
                        {r.originalWrong ? (
                          <>
                            <span className="line-through text-rose-500">
                              {inr(r.storedOriginal)}
                            </span>
                            <span className="block font-bold text-slate-900">
                              {inr(r.correctOriginal)}
                            </span>
                          </>
                        ) : (
                          inr(r.original)
                        )}
                      </td>
                      <td className="p-2 text-right text-emerald-700">{inr(r.collected)}</td>
                      <td className="p-2 text-right text-slate-500">{inr(r.waiver)}</td>
                      <td className="p-2 text-right text-rose-600">{inr(r.storedOutstanding)}</td>
                      <td className="p-2 text-right font-bold text-slate-900">
                        {inr(r.correctOutstanding)}
                      </td>
                      <td className="p-2 text-right text-rose-600">{inr(r.storedOverdue)}</td>
                      <td className="p-2 text-right font-bold text-slate-900">
                        {inr(r.correctOverdue)}
                      </td>
                      <td
                        className={`p-2 text-right font-bold ${
                          (r.difference || r.overdueDifference) >= 0
                            ? 'text-amber-700'
                            : 'text-blue-700'
                        }`}
                      >
                        {(r.difference || r.overdueDifference) >= 0 ? '+' : ''}
                        {inr(r.difference || r.overdueDifference)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={apply}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white font-bold text-[11px] transition cursor-pointer inline-flex items-center gap-1.5"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <span>Repair these {rows.length.toLocaleString('en-IN')} account(s)</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * COMMISSIONS WITH NO COLLECTION BEHIND THEM.
 *
 * Left behind by an older delete that removed the collection and then
 * abandoned the rest of its work. They are still counted in the payout
 * total, so an agent can be billed for commission on a collection that no
 * longer exists.
 *
 * Shows them before removing them. Deleting a commission that IS earned
 * would take money from an agent, so nothing goes until it has been read.
 */
const OrphanCommissionCleanup: React.FC = () => {
  const { previewOrphanedCommissions, cleanOrphanedCommissions, currentUser } = useSRMS();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (currentUser.role !== 'admin' && (currentUser.role as string) !== 'super_admin') return null;

  const inr = (n: number) => '\u20b9' + Math.round(Number(n) || 0).toLocaleString('en-IN');

  const check = () => {
    setBusy(true);
    setDone(null);
    try {
      setResult(previewOrphanedCommissions());
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!result || result.orphans.length === 0) return;
    const value = result.totalNow - result.totalAfter;
    const ok = window.confirm(
      `Remove ${result.orphans.length} commission record(s)?\n\n` +
        `Each one was earned on a collection that no longer exists.\n\n` +
        `Payout total now:   ${inr(result.totalNow)}\n` +
        `After removing:     ${inr(result.totalAfter)}\n` +
        `Difference:         ${inr(value)}\n\n` +
        'This cannot be undone. The collections themselves are not touched.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await cleanOrphanedCommissions();
      setDone(res.message);
      setResult(previewOrphanedCommissions());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center gap-2">
        <GitMerge className="w-4 h-4 text-amber-600" />
        <div>
          <span className="font-bold text-slate-900 block">
            Commissions with no collection behind them
          </span>
          <span className="text-slate-500 text-[11px]">
            An older version of the collection delete removed the collection and then
            abandoned the rest of its work, leaving the commission behind. Those records
            are still counted in the Total Reconciled Payout, so an agent can be billed
            for commission on a collection that does not exist. This finds them.
          </span>
        </div>
      </div>

      {done && (
        <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-[11px] text-emerald-900 font-semibold">
          {done}
        </div>
      )}

      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2 text-[11px]">
        <button
          type="button"
          onClick={check}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 disabled:opacity-40 text-white font-bold text-[11px] transition cursor-pointer inline-flex items-center gap-1.5"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <span>Check the commission ledger (changes nothing)</span>
        </button>

        {result && result.orphans.length === 0 && (
          <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-900 font-bold">
            Every commission has a collection behind it. Payout total {inr(result.totalNow)} is
            correct.
          </div>
        )}

        {result && result.orphans.length > 0 && (
          <div className="space-y-2">
            <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 space-y-0.5">
              <div className="font-bold">
                {result.orphans.length.toLocaleString('en-IN')} commission record(s) have no
                collection behind them. Nothing has been changed yet.
              </div>
              <div className="font-mono">
                Payout total now: <strong>{inr(result.totalNow)}</strong> &nbsp;&rarr;&nbsp; after
                removing: <strong>{inr(result.totalAfter)}</strong> &nbsp;(overstated by{' '}
                <strong>{inr(result.totalNow - result.totalAfter)}</strong>)
              </div>
            </div>

            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-left">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0">
                  <tr>
                    <th className="p-2">Date</th>
                    <th className="p-2">Agent</th>
                    <th className="p-2">Account</th>
                    <th className="p-2 text-right">Collection</th>
                    <th className="p-2 text-right">Commission</th>
                    <th className="p-2">Why</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.orphans.map((o: any) => (
                    <tr key={`orph-${o.id}`} className="hover:bg-slate-50">
                      <td className="p-2 font-mono text-slate-600">{o.date}</td>
                      <td className="p-2 font-semibold text-slate-800">{o.agentName}</td>
                      <td className="p-2 font-mono text-blue-700">{o.accountId}</td>
                      <td className="p-2 text-right font-mono text-slate-600">
                        {inr(o.recoveryAmount)}
                      </td>
                      <td className="p-2 text-right font-mono font-bold text-rose-600">
                        {inr(o.commissionAmount)}
                      </td>
                      <td className="p-2 text-slate-500">{o.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={apply}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white font-bold text-[11px] transition cursor-pointer inline-flex items-center gap-1.5"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <span>
                Remove these {result.orphans.length.toLocaleString('en-IN')} record(s)
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AccountBankBackfill: React.FC = () => {
  const { accounts, accountsScope, backfillAccountBanks, currentUser } = useSRMS();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  if (currentUser.role !== 'admin') return null;

  const missing = accounts.filter((a) => !String((a as any).bank || '').trim()).length;

  const run = () => {
    setBusy(true);
    try {
      const res = backfillAccountBanks();
      setNote(res.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-emerald-600" />
        <div>
          <span className="font-bold text-slate-900 block">Fill in bank from branch</span>
          <span className="text-slate-500 text-[11px]">
            Accounts uploaded before the bank field existed have a branch but no bank, so
            filtering by bank skips them. This copies the bank from each account&apos;s own
            branch record. Nothing is guessed: an account whose branch is not linked to a
            bank is left alone.
          </span>
        </div>
      </div>

      {note && (
        <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-900 font-semibold">
          {note}
        </div>
      )}

      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2 text-[11px]">
        <div className="text-slate-600">
          Currently loaded: <strong>{accounts.length.toLocaleString('en-IN')}</strong> account(s)
          {accountsScope?.label ? ` — ${accountsScope.label}` : ''}.{' '}
          {missing > 0 ? (
            <span className="text-amber-800 font-bold">
              {missing.toLocaleString('en-IN')} with no bank.
            </span>
          ) : (
            <span className="text-emerald-700 font-bold">All of them have a bank.</span>
          )}
        </div>
        <div className="text-slate-400">
          This repairs the accounts loaded right now. To repair every account in one pass,
          use “Load all accounts” in Loan Accounts first.
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy || missing === 0}
          className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-[11px] transition cursor-pointer inline-flex items-center gap-1.5"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <span>Fill in bank on {missing.toLocaleString('en-IN')} account(s)</span>
        </button>
      </div>
    </div>
  );
};

/**
 * Merging duplicate branch records.
 *
 * Excel imports spell the same branch several ways -- a trailing space,
 * different capitals, an abbreviation -- and each spelling became its own
 * branch. That splits a branch manager's accounts across records and makes a
 * branch-wise bill cover one spelling instead of the whole branch.
 *
 * Only likely duplicates are offered: branches of the same bank whose names
 * match once case and spacing are ignored. Unrelated branches are never
 * proposed, because merging cannot be undone.
 */
/**
 * FULL BACKUP.
 *
 * Downloads one Excel file holding everything that has been DONE in the app:
 * collections, commissions, follow-ups, PTPs, visits, agent remarks, OTS
 * history, users, banks, zones, branches and the audit trail -- with the
 * customer name and branch beside every account number.
 *
 * Accounts nothing has happened on are left out, which is what keeps the file
 * small on a book of 200,000. They can be re-imported from the bank's own
 * sheet; the work cannot.
 */
const FullBackup: React.FC = () => {
  const { downloadFullBackup, accounts, accountsScope, currentUser, lastBackupTime } = useSRMS();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  if (currentUser.role !== 'admin' && (currentUser.role as string) !== 'super_admin') return null;

  const run = () => {
    setBusy(true);
    setDone(null);
    try {
      const res = downloadFullBackup();
      setDone(
        `Saved ${res.filename} — ${res.accountsIncluded.toLocaleString('en-IN')} account(s) ` +
          `with activity included, ${res.accountsSkipped.toLocaleString('en-IN')} with none left out.`
      );
    } catch (err: any) {
      setDone(`The backup could not be created: ${err?.message || 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center gap-2">
        <Download className="w-4 h-4 text-emerald-600" />
        <div>
          <span className="font-bold text-slate-900 block">Full backup</span>
          <span className="text-slate-500 text-[11px]">
            One Excel file with everything that has been done: collections, commissions,
            follow-ups, PTPs, visits, agent remarks, OTS history, users, banks, zones,
            branches and the audit trail. Every row carries the customer name and branch
            beside the account number. Accounts nothing has happened on are left out, so
            the file stays small &mdash; those can be re-imported from the bank&apos;s own
            sheet, but the work cannot.
          </span>
        </div>
      </div>

      {done && (
        <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-[11px] text-emerald-900 font-semibold">
          {done}
        </div>
      )}

      {/* Nothing backs this data up on its own. Said plainly, because this
          screen used to show a date from an "automatic backup" that saved
          four numbers and no data at all. */}
      <div
        className={`p-2 rounded-lg text-[11px] font-semibold border ${
          lastBackupTime
            ? 'bg-slate-50 border-slate-200 text-slate-700'
            : 'bg-amber-50 border-amber-300 text-amber-900'
        }`}
      >
        {lastBackupTime
          ? `Last backup downloaded on this computer: ${lastBackupTime}.`
          : 'No backup has been downloaded on this computer yet. Nothing backs this data up on its own.'}
      </div>

      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2 text-[11px]">
        <div className="text-slate-600">
          Currently loaded: <strong>{accounts.length.toLocaleString('en-IN')}</strong> account(s)
          {accountsScope?.label ? ` — ${accountsScope.label}` : ''}.
        </div>
        <div className="text-slate-400">
          The backup covers the accounts loaded right now. To back up the whole book, load
          them all in Loan Accounts first. The file downloads to this computer; nothing is
          uploaded, and it costs no database reads.
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy || accounts.length === 0}
          className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold text-[11px] transition cursor-pointer inline-flex items-center gap-1.5"
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          <span>{busy ? 'Building the file…' : 'Download full backup'}</span>
        </button>
      </div>
    </div>
  );
};

const BranchMergeSettings: React.FC = () => {
  const { branches, mergeBranches, accounts, currentUser } = useSRMS();
  const [note, setNote] = useState<string | null>(null);

  const isAdmin = currentUser.role === 'admin';

  const accountCountFor = (branchName: string) =>
    accounts.filter(
      (a) => (a.branch || '').trim().toLowerCase() === branchName.trim().toLowerCase()
    ).length;

  const duplicateGroups = useMemo(() => {
    const map = new Map<string, typeof branches>();
    (branches || []).forEach((b) => {
      const key = `${(b.bankName || '').trim().toLowerCase()}|${(b.name || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')}`;
      const list = map.get(key) || [];
      list.push(b);
      map.set(key, list);
    });
    return Array.from(map.entries())
      .filter(([, list]) => list.length > 1)
      .map(([key, list]) => ({ key, list }));
  }, [branches]);

  if (!isAdmin) return null;

  const handleMerge = (keepId: string, others: string[]) => {
    const res = mergeBranches(keepId, others);
    setNote(res.message);
    window.setTimeout(() => setNote(null), 6000);
  };

  return (
    <>
    <NoticeBannerSettings />

    {/* Balances first: a wrong figure in front of a borrower matters more
        than a missing bank name. */}
    <BalanceRepair />

    <OrphanCommissionCleanup />

    <AccountBankBackfill />

    <FullBackup />

    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
      <div className="flex items-center gap-2">
        <GitMerge className="w-4 h-4 text-indigo-600" />
        <div>
          <span className="font-bold text-slate-900 block">Duplicate Branches</span>
          <span className="text-slate-500 text-[11px]">
            Same bank, same branch name written differently. Merging moves the
            accounts onto the branch you keep and cannot be undone.
          </span>
        </div>
      </div>

      {note && (
        <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-900 font-semibold">
          {note}
        </div>
      )}

      {duplicateGroups.length === 0 ? (
        <div className="p-3 bg-white rounded-xl border border-slate-200 text-[11px] text-slate-500">
          No duplicate branch names found.
        </div>
      ) : (
        <div className="space-y-2">
          {duplicateGroups.map(({ key, list }) => (
            <div
              key={key}
              className="p-3 bg-white rounded-xl border border-amber-200 space-y-2"
            >
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>
                  {list.length} records for “{list[0].name}”
                  {list[0].bankName ? ` — ${list[0].bankName}` : ''}
                </span>
              </div>

              <div className="space-y-1.5">
                {list.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center justify-between gap-2 text-[11px] border border-slate-100 rounded-lg px-2.5 py-1.5"
                  >
                    <span className="font-mono">
                      “{b.name}”
                      <span className="ml-2 text-slate-400">
                        {b.code || '—'} · {accountCountFor(b.name)} account(s)
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleMerge(
                          b.id,
                          list.filter((o) => o.id !== b.id).map((o) => o.id)
                        )
                      }
                      className="px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-[10px] transition cursor-pointer shrink-0"
                    >
                      Keep this one
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
};

export default BranchMergeSettings;
